import { Bank } from "./bank";
import { BankRepository } from "./bankRepository";
import { LocalCache } from "../../core/cache/local-cache";

const CACHE_KEY = "banks";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

type BankRaw = { ispb: string; name: string; code: number; fullName: string };

/**
 * Implementação de {@link BankRepository} utilizando a BrasilAPI e IndexedDB como cache local.
 */
export class BankRepositoryImpl implements BankRepository {
  private static readonly cache = LocalCache.getInstance(CACHE_KEY, TTL_MS);

  /**
   * Filtra bancos pelo código COMPE numérico ou por nome completo (texto parcial).
   * @param query Código numérico ou texto parcial do nome do banco
   * @returns Lista de bancos correspondentes
   */
  async filterByCodeOrFullName(query: string): Promise<Bank[]> {
    const banks = await this.getBanks();
    return banks.filter(
      (b) =>
        b.fullName.toUpperCase().includes(query.toUpperCase()) ||
        b.code === parseInt(query)
    );
  }

  /**
   * Busca bancos cujo nome completo contenha o texto informado.
   * @param query Texto parcial do nome do banco (insensível a maiúsculas)
   * @returns Lista de bancos correspondentes
   */
  async findByFullName(query: string): Promise<Bank[]> {
    const banks = await this.getBanks();
    return banks.filter((b) =>
      b.fullName.toUpperCase().includes(query.toUpperCase())
    );
  }

  /**
   * Retorna todos os bancos disponíveis.
   * @returns Lista completa de bancos
   */
  async list(): Promise<Bank[]> {
    return await this.getBanks();
  }

  /**
   * Obtém a lista de bancos do IndexedDB (cache local com TTL de 7 dias) ou da BrasilAPI.
   * Persiste o resultado no cache para evitar requisições repetidas.
   * @returns Lista de bancos válidos (com ISPB, name, code e fullName preenchidos)
   * @throws {Error} Quando a requisição à BrasilAPI falha
   */
  private async getBanks(): Promise<Bank[]> {
    const cached = await BankRepositoryImpl.cache.get<BankRaw[]>(CACHE_KEY);
    if (cached) {
      return cached.map(Bank.from);
    }
    const response = await fetch("https://brasilapi.com.br/api/banks/v1");
    if (!response.ok) {
      throw new Error("Falha na consulta à API");
    }
    const rawBanks = this.filterValidBanks(await response.json());
    await BankRepositoryImpl.cache.set(CACHE_KEY, rawBanks);
    return rawBanks.map(Bank.from);
  }

  /**
   * Remove registros brutos com campos obrigatórios ausentes (ISPB, name, code ou fullName).
   * @param banks Lista bruta de bancos retornada pela API
   * @returns Lista filtrada somente com registros completos
   */
  private filterValidBanks(banks: BankRaw[]) {
    return banks.filter(
      (bank) => bank.ispb && bank.name && bank.code && bank.fullName
    );
  }
}
